import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause } from 'lucide-react';
import { DEMO_SCREENS } from './landingData';

/* ─── Animated Product Walkthrough ─── */
export function ProductShowcase() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);
  const next = useCallback(() => setActive(prev => (prev + 1) % DEMO_SCREENS.length), []);

  useEffect(() => {
    if (paused) return;
    timerRef.current = setInterval(next, 4500);
    return () => clearInterval(timerRef.current);
  }, [paused, next]);

  const screen = DEMO_SCREENS[active];

  return (
    <div className="max-w-6xl mx-auto mt-20 px-4" data-testid="product-showcase">
      {/* Section Header */}
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#ECFDF5] to-[#ECFDF5] border border-[#DCE8FF] rounded-full px-4 py-1.5 mb-4">
          <Play className="w-3.5 h-3.5 text-[#065F46]" />
          <span className="text-[12px] font-semibold text-[#065F46] tracking-wide uppercase">Live Product Demo</span>
        </div>
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: "'SF Pro Display', -apple-system, sans-serif", color: '#1D1D1F' }}>
          See Smarto In Action
        </h2>
        <p className="text-[16px] text-[#86868B] mt-2 max-w-xl mx-auto">Watch how each module works together to run your entire business from one platform</p>
      </div>

      {/* Tab Bar */}
      <div className="flex items-center justify-center gap-1 mb-6 flex-wrap">
        {DEMO_SCREENS.map((s, i) => (
          <button key={s.id} onClick={() => { setActive(i); setPaused(false); }}
            className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[12px] font-medium transition-all ${i === active ? 'bg-[#1D1D1F] text-white shadow-lg shadow-black/10 scale-105' : 'bg-[#F5F5F7] text-[#86868B] hover:bg-gray-200 hover:text-gray-600'}`}
            data-testid={`showcase-tab-${s.id}`}>
            <s.icon className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{s.label}</span>
          </button>
        ))}
        <button onClick={() => setPaused(p => !p)}
          className="ml-2 w-8 h-8 rounded-full bg-[#F5F5F7] hover:bg-gray-200 flex items-center justify-center transition-colors"
          data-testid="showcase-pause-btn">
          {paused ? <Play className="w-3.5 h-3.5 text-[#86868B]" /> : <Pause className="w-3.5 h-3.5 text-[#86868B]" />}
        </button>
      </div>

      {/* Video-style Browser Chrome */}
      <div className="rounded-2xl overflow-hidden border border-gray-200/80 shadow-[0_20px_80px_rgb(0,0,0,0.12)] bg-[#F5F5F7]">
        {/* Browser Top Bar */}
        <div className="h-10 bg-gradient-to-b from-[#E8E8ED] to-[#E2E2E7] flex items-center px-4 gap-2">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-[#FF5F57] shadow-inner" />
            <div className="w-3 h-3 rounded-full bg-[#FEBC2E] shadow-inner" />
            <div className="w-3 h-3 rounded-full bg-[#28C840] shadow-inner" />
          </div>
          <div className="flex-1 flex justify-center">
            <div className="bg-white/90 rounded-lg px-5 py-1 text-[11px] text-[#86868B] font-medium flex items-center gap-1.5 shadow-sm">
              <svg className="w-3 h-3 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
              smarto.com/{screen.id}
            </div>
          </div>
        </div>

        {/* Screen Content with Overlay */}
        <div className="relative aspect-[16/9] bg-white overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.img key={screen.id} src={screen.src} alt={screen.label}
              className="w-full h-full object-cover object-top"
              initial={{ opacity: 0, scale: 1.05 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.97 }}
              transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }} />
          </AnimatePresence>

          {/* Gradient Caption Overlay */}
          <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-8">
            <div className="flex items-end justify-between">
              <AnimatePresence mode="wait">
                <motion.div key={screen.id}
                  initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -15 }}
                  transition={{ duration: 0.4, delay: 0.1 }}>
                  <div className="flex items-center gap-2 mb-2">
                    <screen.icon className="w-5 h-5 text-[#D97706]" />
                    <span className="text-[#D97706] text-[11px] font-bold uppercase tracking-[0.15em]">{screen.label}</span>
                  </div>
                  <p className="text-white text-[17px] font-semibold max-w-md leading-snug">{screen.caption}</p>
                </motion.div>
              </AnimatePresence>

              {/* Floating Stat Card */}
              <AnimatePresence mode="wait">
                <motion.div key={`stat-${screen.id}`}
                  className="bg-white/15 backdrop-blur-xl rounded-xl px-5 py-3 text-right border border-white/20"
                  initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.5, delay: 0.2 }}>
                  <div className="text-white text-2xl font-black tracking-tight">{screen.stat}</div>
                  <div className="text-white/60 text-[10px] font-medium uppercase tracking-wider mt-0.5">{screen.statLabel}</div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Progress Track */}
        <div className="h-1.5 bg-[#E8E8ED] flex">
          {DEMO_SCREENS.map((_, i) => (
            <div key={i} className="flex-1 relative overflow-hidden">
              {i === active && !paused && (
                <motion.div className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#065F46] to-[#10B981]"
                  initial={{ width: '0%' }} animate={{ width: '100%' }}
                  transition={{ duration: 4.5, ease: 'linear' }} key={`progress-${active}-${Date.now()}`} />
              )}
              {i < active && <div className="absolute inset-0 bg-gradient-to-r from-[#065F46] to-[#10B981]" />}
              {i === active && paused && <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#065F46] to-[#10B981]" style={{ width: '50%' }} />}
            </div>
          ))}
        </div>
      </div>

      {/* CTA Under Demo */}
      <div className="flex items-center justify-center gap-4 mt-8">
        <button onClick={() => setPaused(p => !p)} className="flex items-center gap-2 text-[13px] font-medium text-[#86868B] hover:text-[#1D1D1F] transition-colors" data-testid="demo-play-toggle">
          {paused ? <><Play className="w-4 h-4" /> Resume Demo</> : <><Pause className="w-4 h-4" /> Pause Demo</>}
        </button>
        <span className="text-gray-300">|</span>
        <span className="text-[12px] text-[#86868B]">{active + 1} / {DEMO_SCREENS.length} screens</span>
      </div>
    </div>
  );
}
